import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  FaFacebookF,
  FaYoutube,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaGraduationCap,
  FaArrowRight,
} from "react-icons/fa";

import { HiOutlineMenuAlt3 } from "react-icons/hi";
import { IoClose } from "react-icons/io5";

import logo from "./cayeplogo.jpg";
import "./NavbarCayep.css";

const navLinks = [
  {
    text: "Home",
    path: "/",
  },
  {
    text: "Leadership Message",
    path: "/principalMessagePage",
  },
  {
    text: "Training Programs",
    path: "/campusPage",
  },

  {
    text: "Login",
    path: "/login",
    button: true,
  },
];

const Navbar = () => {
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const mobileMenuRef = useRef(null);
  const mobileToggleRef = useRef(null);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.classList.add("navbar-menu-open");
    } else {
      document.body.classList.remove("navbar-menu-open");
    }

    return () => {
      document.body.classList.remove("navbar-menu-open");
    };
  }, [menuOpen]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && menuOpen) {
        setMenuOpen(false);
        mobileToggleRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!menuOpen) return;

      const clickedInsideMenu =
        mobileMenuRef.current?.contains(event.target);

      const clickedToggle =
        mobileToggleRef.current?.contains(event.target);

      if (!clickedInsideMenu && !clickedToggle) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [menuOpen]);

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }

    return location.pathname === path;
  };

  const toggleMenu = () => {
    setMenuOpen((previous) => !previous);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  return (
    <>
      <header
        className={`site-header ${
          scrolled ? "site-header-scrolled" : ""
        }`}
      >
        <div className="top-navbar">
          <div className="top-navbar-inner">
            <div className="top-navbar-left">
              <div className="top-item brand-tag-item">
                <FaGraduationCap />

                <span>
                  CAYEP • LEADERSHIP TRAINING
                </span>
              </div>

              <div className="top-item location-item">
                <FaMapMarkerAlt />

                <span>
              Zayzay Community Monrovia, Liberia
                </span>
              </div>
            </div>

            <div className="top-navbar-right">
              <span className="follow-text">
                Connect With CAYEP
              </span>

              <div className="top-socials">
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="CAYEP on Facebook"
                >
                  <FaFacebookF />
                </a>

                <a
                  href="https://youtube.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="CAYEP on YouTube"
                >
                  <FaYoutube />
                </a>

                <a
                  href="mailto:info@cayep.edu"
                  aria-label="Email CAYEP"
                >
                  <FaEnvelope />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="navbar-main">
          <div className="navbar-main-inner">
            <Link
              to="/"
              className="logo-section"
              aria-label="CAYEP Leadership Training Home"
            >
              <div className="logo-wrapper">
                <img
                  src={logo}
                  alt="CAYEP Leadership Training"
                  className="navbar-logo"
                />
              </div>

              <div className="school-name">
                <h1>CAYEP</h1>

                <p>
                  Leadership Training
                </p>

                <span className="brand-motto">
                  EDUCATE • EQUIP • EMPOWER
                </span>
              </div>
            </Link>

            <nav
              className="desktop-navigation"
              aria-label="CAYEP main navigation"
            >
              <ul className="nav-links">
                {navLinks.map((link) => (
                  <li
                    key={link.path}
                    className={
                      isActive(link.path)
                        ? "active"
                        : ""
                    }
                  >
                    <Link
                      to={link.path}
                      className={
                        link.button
                          ? "login-button"
                          : ""
                      }
                    >
                      <span>{link.text}</span>

                      {link.button && (
                        <FaArrowRight />
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <button
              ref={mobileToggleRef}
              type="button"
              className={`mobile-toggle ${
                menuOpen ? "toggle-active" : ""
              }`}
              onClick={toggleMenu}
              aria-label={
                menuOpen
                  ? "Close CAYEP navigation menu"
                  : "Open CAYEP navigation menu"
              }
              aria-expanded={menuOpen}
              aria-controls="mobile-navigation"
            >
              {menuOpen ? (
                <IoClose />
              ) : (
                <HiOutlineMenuAlt3 />
              )}
            </button>
          </div>
        </div>

        <div
          className={`mobile-backdrop ${
            menuOpen ? "show" : ""
          }`}
          onClick={closeMenu}
          aria-hidden="true"
        />

        <aside
          ref={mobileMenuRef}
          id="mobile-navigation"
          className={`mobile-menu ${
            menuOpen ? "show" : ""
          }`}
          aria-hidden={!menuOpen}
        >
          <div className="mobile-menu-header">
            <div className="mobile-brand">
              <img
                src={logo}
                alt="CAYEP Leadership Training"
              />

              <div>
                <strong>CAYEP</strong>

                <span>
                  Leadership Training
                </span>
              </div>
            </div>

            <button
              type="button"
              className="mobile-close-button"
              onClick={closeMenu}
              aria-label="Close CAYEP navigation menu"
            >
              <IoClose />
            </button>
          </div>

          <div className="mobile-menu-divider" />

          <div className="mobile-brand-message">
            <FaGraduationCap />

            <div>
              <strong>
                Learn • Lead • Impact
              </strong>

              <span>
                Building confident leaders for
                stronger communities.
              </span>
            </div>
          </div>

          <nav
            className="mobile-navigation"
            aria-label="CAYEP mobile navigation"
          >
            <ul>
              {navLinks.map((link) => (
                <li
                  key={link.path}
                  className={
                    isActive(link.path)
                      ? "active"
                      : ""
                  }
                >
                  <Link
                    to={link.path}
                    onClick={closeMenu}
                    tabIndex={menuOpen ? 0 : -1}
                    className={
                      link.button
                        ? "mobile-login-button"
                        : ""
                    }
                  >
                    <span>{link.text}</span>

                    {link.button ? (
                      <FaArrowRight />
                    ) : (
                      <span className="mobile-arrow">
                        →
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="mobile-contact">
            <div className="mobile-contact-heading">
              <span>CONTACT CAYEP</span>
            </div>

            <div className="mobile-contact-item">
              <FaMapMarkerAlt />

              <span>
                Monrovia, Liberia
              </span>
            </div>

            <div className="mobile-contact-item">
              <FaPhoneAlt />

              <span>
                Leadership Training & Support
              </span>
            </div>
          </div>

          <div className="mobile-menu-divider" />

          <div className="mobile-social-section">
            <span>
              Connect With CAYEP
            </span>

            <div className="mobile-socials">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="CAYEP on Facebook"
              >
                <FaFacebookF />
              </a>

              <a
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="CAYEP on YouTube"
              >
                <FaYoutube />
              </a>

              <a
                href="mailto:info@cayep.edu"
                aria-label="Email CAYEP"
              >
                <FaEnvelope />
              </a>
            </div>
          </div>

          <div className="mobile-menu-footer">
            <span>
              CAYEP
            </span>

            <small>
              Leadership Training
            </small>

            <em>
              Educate • Equip • Empower
            </em>
          </div>
        </aside>
      </header>

      <div className="navbar-spacing" />
    </>
  );
};

export default Navbar;